import React from 'react';
import Asterisk from '@shared/components/Asterisk';
import SkeletonWrapper from '@shared/components/skeleton/SkeletonWrapper';
import DatePicker from 'react-datepicker';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { addDays } from '@shared/services/dateService';
import moment from 'moment';

const DEFAULT_END_DATE_RANGE = 7;
const INTENDED_START_DATE_RESTRICTION_WEEKS = 2;

/**
 * @typedef {object} EventDatesProps
 * @property {Date} [selectedStartDate]
 * @property {Date} [selectedEndDate]
 * @property {string} [intendedStartDate]
 * @property {(startDate: Date, endDate: Date) => void} [onDateRangeChanged]
 * @property {boolean} [loading]
 * @property {Array<*>} [excludedDateIntervals]
 * @property {*} [minDate]
 * @property {boolean} [disabled]
 * @property {string} label
 * @property {string} name
 * @property {string} [placeholder]
 * @property {boolean} [isRequired]
 */

/**
 * @param {EventDatesProps} props
 */
const EventDates = props => {
  const {
    selectedStartDate,
    selectedEndDate,
    onDateRangeChanged,
    intendedStartDate,
    minDate,
    label,
    name,
    loading = false,
    excludedDateIntervals = [],
    disabled = false,
    placeholder = '',
    isRequired = true
  } = props;

  const handleDateRangeChange = dates => {
    onDateRangeChanged(dates[0], dates[1]);
  };

  const handleCalendarClose = () => {
    if (selectedEndDate) {
      return;
    }

    onDateRangeChanged(
      selectedStartDate,
      getEndDateBySelectedStartDate(selectedStartDate, excludedDateIntervals)
    );
  };

  const dateRestictionProps = getDatePickerDateRestrictionsProps(
    selectedStartDate,
    selectedEndDate,
    minDate,
    intendedStartDate,
    excludedDateIntervals
  );

  return (
    <section className='page-section' data-testid={name}>
      <div className='section-title bold'>
        <span className='date-label'>
          {label}
          {isRequired && <Asterisk />}
        </span>
      </div>
      <div className='option-selector'>
        <div className='date-picker-wrapper'>
          <div className='custom-date-picker-input'>
            <SkeletonWrapper show={loading} inheritParentSize={true}>
              <DatePicker
                placeholderText={placeholder}
                selectsRange={true}
                className='date-picker'
                dateFormat='yyyy-MM-dd'
                inline={false}
                onChange={handleDateRangeChange}
                onCalendarClose={handleCalendarClose}
                onKeyDown={e => {
                  e.preventDefault();
                }}
                disabled={disabled}
                {...dateRestictionProps}
              />
            </SkeletonWrapper>
          </div>
          <CalendarTodayIcon />
        </div>
      </div>
    </section>
  );
};

const getDatePickerDateRestrictionsProps = (
  selectedStartDate,
  selectedEndDate,
  minimumRangeDate,
  intendedStartDate,
  excludedDateIntervals
) => {
  const startDateMinimumByIntendedDate = intendedStartDate
    ? moment(intendedStartDate).subtract(INTENDED_START_DATE_RESTRICTION_WEEKS, 'weeks')
    : null;
  const startDateMaximumByIntendedDate = intendedStartDate
    ? moment(intendedStartDate).add(INTENDED_START_DATE_RESTRICTION_WEEKS, 'weeks')
    : null;

  const includedDateIntervals = getIncludedDateIntervals(
    selectedStartDate,
    selectedEndDate,
    startDateMinimumByIntendedDate,
    startDateMaximumByIntendedDate
  );
  const maxDateInternal = getMaxRangeDate(
    selectedStartDate,
    selectedEndDate,
    startDateMaximumByIntendedDate,
    excludedDateIntervals
  );
  const minDateInternal = getMinRangeDate(minimumRangeDate, startDateMinimumByIntendedDate);

  return {
    includeDateIntervals: includedDateIntervals,
    excludeDateIntervals: excludedDateIntervals,
    minDate: minDateInternal,
    maxDate: maxDateInternal,
    startDate: selectedStartDate,
    endDate: selectedEndDate
  };
};

const getEndDateBySelectedStartDate = (startDate, excludedDateIntervals) => {
  const maxEndDateForStartDate = getMaxEndDateForStartDate(startDate, excludedDateIntervals);
  const endDate = addDays(startDate, DEFAULT_END_DATE_RANGE - 1);

  return maxEndDateForStartDate && maxEndDateForStartDate <= endDate
    ? addDays(maxEndDateForStartDate, -1)
    : endDate;
};

const getMaxEndDateForStartDate = (startDate, excludedDateIntervals) =>
  excludedDateIntervals.find(interval => startDate < interval.start)?.start;

const getMinRangeDate = (minDate, minStartDateByIntededDate) => {
  if (minDate && minStartDateByIntededDate) {
    return moment.max([moment(minDate), minStartDateByIntededDate]).toDate();
  }

  return minDate || minStartDateByIntededDate;
};

const getMaxRangeDate = (
  selectedStartDate,
  selectedEndDate,
  maxStartDateByIntededDate,
  excludedDateIntervals
) => {
  if (isOnlyStartDateSelected(selectedStartDate, selectedEndDate)) {
    return getMaxEndDateForStartDate(selectedStartDate, excludedDateIntervals);
  }

  return maxStartDateByIntededDate?.toDate() ?? null;
};

const getIncludedDateIntervals = (
  selectedStartDate,
  selectedEndDate,
  minStartDate,
  maxStartDate
) => {
  if (isOnlyStartDateSelected(selectedStartDate, selectedEndDate) || !minStartDate) {
    return null;
  }

  return [
    {
      start: minStartDate.toDate(),
      end: maxStartDate.toDate()
    }
  ];
};
const isOnlyStartDateSelected = (startDate, endDate) => startDate && !endDate;

export default EventDates;
